import {blobToBase64} from 'client/services/blobToBase64';
import {fetchFile} from 'client/services/helpers';

import {MEDIA_MIME_TYPES} from 'client/common/config';

const MIME_BY_EXTENSION: Record<string, string> = {
  html: MEDIA_MIME_TYPES.html,
  htm: MEDIA_MIME_TYPES.html,
  pdf: MEDIA_MIME_TYPES.pdf,
  jpg: MEDIA_MIME_TYPES.jpg,
  jpeg: MEDIA_MIME_TYPES.jpeg,
  png: MEDIA_MIME_TYPES.png,
  mp3: MEDIA_MIME_TYPES.mp3,
  mp4: MEDIA_MIME_TYPES.mp4,
};

const getExtensionFromFilename = (filename = '') => {
  const [namePart = ''] = filename.split('?');
  const extension = namePart.split('.').pop();

  return extension?.toLowerCase() || '';
};

const isHtmlBlobContent = async (blob: Blob) => {
  if (!blob.size) {
    return false;
  }

  const headChunk = blob.slice(0, 2048);
  const text = (await headChunk.text()).trim().toLowerCase();

  return text.startsWith('<!doctype html') || text.startsWith('<html') || text.includes('<html');
};

export const resolveFileMimeType = async (res: Response, blob: Blob, filename = '') => {
  const responseMimeType = res.headers.get('content-type')?.split(';')?.[0]?.trim()?.toLowerCase() || '';
  const extension = getExtensionFromFilename(filename);
  const mimeFromExtension = MIME_BY_EXTENSION[extension] || '';

  if (responseMimeType && responseMimeType !== MEDIA_MIME_TYPES.app) {
    return responseMimeType;
  }

  if (mimeFromExtension) {
    return mimeFromExtension;
  }

  if (blob.type && blob.type !== MEDIA_MIME_TYPES.app) {
    return blob.type;
  }

  if (await isHtmlBlobContent(blob)) {
    return MEDIA_MIME_TYPES.html;
  }

  return blob.type || MEDIA_MIME_TYPES.app;
};

export async function getImageProperties(file: string | File) {
  const media = typeof file === 'string' ? await toFile(file) : file;

  const img = new Image();
  const data = await blobToBase64(media);

  const promise = new Promise<{
    data: Awaited<ReturnType<typeof blobToBase64>>;
    width: number;
    height: number;
    size: number;
    type: string;
  }>((resolve, reject) => {
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;

      resolve({data, width, height, size: media.size, type: media.type});
    };
    img.onerror = reject;
  });

  img.src = window.URL.createObjectURL(media);

  return promise;
}

export async function toFile(url: string) {
  const filename = getFileName(url);
  const res = url?.startsWith('http') ? await fetchFile(url) : await fetch(url);
  const blob = await res.blob();
  const fileType = await resolveFileMimeType(res, blob, filename);

  return new File([blob], filename, {type: fileType});
}

export function getFileNameFromValue(value: File | string | null | undefined): string | null {
  if (typeof value === 'string') {
    return value ? getFileName(value) || null : null;
  }
  return (value as {name: string} | null)?.name ?? null;
}

export function getFileName(url?: string) {
  const regex = /filename=([^&]*)/;
  const match = url?.match(regex);

  if (!match) {
    return '';
  }

  return url?.match(/filename=([^&]*)/)?.[1] || '';
}
